import { useState } from "react";
import { CreditCard, Landmark, Upload, Loader2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

export const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const CYCLE = { monthly: "Mensual", annual: "Anual" };

function PlanPicker({ plans, planId, setPlanId, cycle, setCycle, fixedAnnual }) {
  return (
    <div className="space-y-3">
      {!fixedAnnual && <div className="inline-flex rounded-xl bg-[#111827]/5 p-1" data-testid="pay-cycle-toggle">
        {Object.entries(CYCLE).map(([k, l]) => (
          <button key={k} data-testid={`pay-cycle-${k}`} onClick={() => setCycle(k)}
            className={`px-4 h-9 rounded-lg text-sm font-semibold transition-colors ${cycle === k ? "bg-white shadow text-[#111827]" : "text-[#111827]/60"}`}>{l}</button>
        ))}
      </div>}
      <div className="grid gap-2 sm:grid-cols-3">
        {plans.map((p) => {
          const price = cycle === "annual" ? p.price_annual : p.price_monthly;
          return (
            <button key={p.id} data-testid={`pay-plan-${p.key}`} onClick={() => setPlanId(p.id)}
              className={`text-left rounded-xl border p-3 transition-colors ${planId === p.id ? "border-[#E85D04] bg-[#E85D04]/5 ring-2 ring-[#E85D04]/25" : "border-[#111827]/10 hover:border-[#111827]/30"}`}>
              <p className="font-bold text-[#111827] flex items-center gap-1">{planId === p.id && <Check size={14} className="text-[#E85D04]" />}{p.name}</p>
              <p className="font-mono font-bold text-[#E85D04]">{money(price)}<span className="text-[11px] text-[#111827]/50"> /{cycle === "annual" ? "año" : "mes"}</span></p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BankData({ cfg }) {
  const rows = [["Banco", cfg.bank_name], ["Titular", cfg.account_holder], ["CLABE", cfg.clabe], ["Cuenta", cfg.account_number]].filter(([, v]) => v);
  return (
    <div className="rounded-xl bg-[#F8FAFC] border border-[#111827]/10 p-3 text-sm space-y-1" data-testid="pay-bank-data">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-[#111827]/60">{k}</span>
          <button onClick={() => { navigator.clipboard?.writeText(v); toast.success(`${k} copiado`); }} className="font-mono font-semibold flex items-center gap-1 hover:text-[#E85D04]">{v} <Copy size={12} /></button>
        </div>
      ))}
      {cfg.instructions && <p className="text-xs text-[#111827]/60 pt-1">{cfg.instructions}</p>}
    </div>
  );
}

export const PayPanel = ({ status, kind = "renew", plan: fixedPlan, onDone }) => {
  const cfg = status?.config || {};
  const active = status?.status !== "expired";
  const toAnnual = kind === "to_annual";
  const plans = (status?.plans || []).filter((p) => (toAnnual ? !!p.annual_quote : (!active || kind !== "renew" || p.id === status?.plan?.id)));
  const inReview = (status?.orders || []).some((o) => o.status === "en_revision");
  const [planId, setPlanId] = useState(fixedPlan?.id || status?.plan?.id || plans[0]?.id);
  const [cycle, setCycle] = useState(toAnnual ? "annual" : (status?.license?.billing_cycle || "monthly"));
  const [method, setMethod] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const plan = fixedPlan || plans.find((p) => p.id === planId);
  const amount = kind === "upgrade" ? fixedPlan?.quote?.amount : toAnnual ? plan?.annual_quote?.amount : plan ? (cycle === "annual" ? plan.price_annual : plan.price_monthly) : 0;
  const body = { kind, plan_id: plan?.id, billing_cycle: kind === "upgrade" ? status?.license?.billing_cycle : cycle };
  const payMp = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/checkout/mp", body); window.location.assign(data.init_point); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  const sendTransfer = async () => {
    if (!file) { toast.error("Adjunta tu comprobante de transferencia"); return; }
    const fd = new FormData();
    Object.entries(body).forEach(([k, v]) => fd.append(k, v || ""));
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/billing/checkout/transfer", fd); toast.success("Comprobante enviado. Te avisaremos cuando se apruebe."); onDone?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!cfg.mp_enabled && !cfg.transfer_enabled) {
    return <p className="text-sm text-[#DC2626]" data-testid="pay-no-methods">Por ahora no hay métodos de pago disponibles. Contacta a tu proveedor.</p>;
  }
  if (inReview) {
    return <p className="text-sm rounded-xl bg-[#F59E0B]/10 text-[#B45309] p-3" data-testid="pay-in-review">Tienes un comprobante en revisión. Podrás hacer otro pago cuando se apruebe o rechace.</p>;
  }
  return (
    <div className="space-y-4" data-testid="pay-panel">
      {(kind === "renew" || toAnnual) && <PlanPicker plans={plans} planId={planId} setPlanId={setPlanId} cycle={cycle} setCycle={setCycle} fixedAnnual={toAnnual} />}
      {toAnnual && plan?.annual_quote && (
        <div className="rounded-xl bg-[#F8FAFC] border border-[#111827]/10 p-3 text-sm space-y-1" data-testid="to-annual-breakdown">
          <div className="flex justify-between"><span>Precio anual {plan.name}</span><span className="font-mono">{money(plan.annual_quote.annual_price)}</span></div>
          <div className="flex justify-between text-[#16A34A]"><span>(-) Saldo a favor de tu mes ({plan.annual_quote.remaining_days} días sin usar)</span><span className="font-mono">{money(plan.annual_quote.credit)}</span></div>
          <p className="text-xs text-[#111827]/50 pt-1">Tu año de servicio empieza el día que se confirme el pago.</p>
          {plan.price_monthly * 12 - plan.price_annual > 0 && (
            <p className="text-xs font-bold text-[#16A34A]" data-testid="to-annual-saving">Ahorras {money(plan.price_monthly * 12 - plan.price_annual)} al año frente a pagar mes por mes.</p>
          )}
        </div>
      )}
      <div className="flex items-center justify-between rounded-xl bg-[#111827] text-white px-4 py-3">
        <span className="text-sm">{kind === "upgrade" ? `Mejora a ${plan?.name}` : toAnnual ? `Pasar a anual · ${plan?.name || ""}` : `${plan?.name || ""} · ${CYCLE[cycle]}`}</span>
        <span className="font-mono text-xl font-extrabold" data-testid="pay-amount">{money(amount)}</span>
      </div>
      <div className={`grid gap-2 ${kind === "upgrade" ? "" : "sm:grid-cols-2"}`}>
        {cfg.mp_enabled && (
          <button data-testid="pay-mp-btn" disabled={busy || !plan} onClick={payMp}
            className="h-12 rounded-xl bg-[#009EE3] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#0084BF] disabled:opacity-60">
            {busy && method !== "transfer" ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Pagar con Mercado Pago
          </button>
        )}
        {cfg.transfer_enabled && (
          <button data-testid="pay-transfer-btn" disabled={busy || !plan} onClick={() => setMethod(method === "transfer" ? null : "transfer")}
            className={`h-12 rounded-xl border font-semibold flex items-center justify-center gap-2 ${method === "transfer" ? "border-[#E85D04] text-[#E85D04] bg-[#E85D04]/5" : "border-[#111827]/15 hover:bg-[#F8FAFC]"}`}>
            <Landmark size={18} /> Pagar por transferencia
          </button>
        )}
      </div>
      {method === "transfer" && (
        <div className="space-y-3" data-testid="pay-transfer-panel">
          <BankData cfg={cfg} />
          <p className="text-xs text-[#111827]/60">Transfiere exactamente <b>{money(amount)}</b> y sube tu comprobante (foto o PDF, máx. 5 MB).</p>
          <label className="flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-[#111827]/25 text-sm cursor-pointer hover:bg-[#F8FAFC]">
            <Upload size={16} /> <span className="truncate">{file ? file.name : "Seleccionar comprobante"}</span>
            <input data-testid="pay-receipt-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <button data-testid="pay-transfer-submit" disabled={busy} onClick={sendTransfer}
            className="w-full h-12 rounded-xl bg-[#E85D04] text-white font-semibold flex items-center justify-center gap-2 hover:bg-[#C2410C] disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />} Enviar comprobante
          </button>
        </div>
      )}
    </div>
  );
};
